import {
  alpha,
  Box,
  Button,
  MenuItem,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { RANGE_TYPE_SLUG, RangeType } from "@pages:app/analytics/utils";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCalendarToday, MdKeyboardArrowDown } from "react-icons/md";

const PRESETS = Object.values(RangeType).filter(
  (rangeType) => rangeType !== RangeType.Custom,
);

type DateRangeDropdownProps = {
  label: string;
  currentRangeType: RangeType;
  fromDate: string | null;
  toDate: string | null;
  onSelectPreset: (rangeType: RangeType) => void;
  onChangeFromDate: (value: DateTime | null) => void;
  onChangeToDate: (value: DateTime | null) => void;
};

export const DateRangeDropdown = ({
  label,
  currentRangeType,
  fromDate,
  toDate,
  onSelectPreset,
  onChangeFromDate,
  onChangeToDate,
}: DateRangeDropdownProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<MdCalendarToday size={15} />}
        endIcon={<MdKeyboardArrowDown size={18} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          minWidth: "170px",
          justifyContent: "space-between",
          borderRadius: "7px",
          color: theme.palette.text.secondary,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderColor: alpha(theme.palette.primary.main, 0.2),
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
      >
        {label}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box sx={{ display: "flex" }}>
          {/* Presets */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 1,
              minWidth: "150px",
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            {PRESETS.map((rangeType) => (
              <MenuItem
                key={rangeType}
                selected={rangeType === currentRangeType}
                onClick={() => {
                  onSelectPreset(rangeType);
                  setAnchorEl(null);
                }}
                sx={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  "&.Mui-selected": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t(`analytics.range.${RANGE_TYPE_SLUG[rangeType]}`)}
              </MenuItem>
            ))}
          </Box>

          {/* Custom range */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme.palette.text.secondary,
              }}
            >
              {t("analytics.customRange")}
            </Typography>
            <DatePicker
              label={t("analytics.startDate")}
              value={fromDate ? DateTime.fromISO(fromDate) : null}
              maxDate={toDate ? DateTime.fromISO(toDate) : undefined}
              disableFuture
              onChange={(value) => onChangeFromDate(value as DateTime | null)}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("analytics.endDate")}
              value={toDate ? DateTime.fromISO(toDate) : null}
              minDate={fromDate ? DateTime.fromISO(fromDate) : undefined}
              disableFuture
              onChange={(value) => onChangeToDate(value as DateTime | null)}
              slotProps={{ textField: { size: "small" } }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};
